import { motion } from 'framer-motion';
import { Zap, TrendingDown, Target, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useRef } from 'react';
import heroVideo from '@/assets/hero-workshop-video.mp4';

const HeroSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const directionRef = useRef<'forward' | 'backward'>('forward');
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Video erst nach dem ersten Rendern laden, damit es LCP/FCP nicht blockiert
    const loadVideo = () => {
      if (!video.getAttribute('src')) {
        video.setAttribute('src', heroVideo);
        video.load();
        video.play().catch(() => undefined);
      }
    };
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    const idleId = idle ? idle(loadVideo, { timeout: 2500 }) : window.setTimeout(loadVideo, 1200);

    video.playbackRate = 0.5;
    directionRef.current = 'forward';

    const startBackward = () => {
      video.pause();
      directionRef.current = 'backward';
      const step = () => {
        if (!video || directionRef.current !== 'backward') return;
        video.currentTime = Math.max(0, video.currentTime - 0.033);
        if (video.currentTime <= 0.05) {
          directionRef.current = 'forward';
          video.currentTime = 0;
          video.play();
          return;
        }
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    };

    // Start backward from midpoint instead of waiting for end
    const handleTimeUpdate = () => {
      if (directionRef.current === 'forward' && video.currentTime >= video.duration / 2) {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        startBackward();
      }
    };

    const handleEnded = () => {
      startBackward();
    };

    // After first backward pass completes and forward play starts again,
    // let it play to the end, then reverse from end
    const handleEndedFull = () => {
      startBackward();
    };

    const onLoadedMetadata = () => {
      video.addEventListener('timeupdate', handleTimeUpdate);
    };

    if (video.readyState >= 1) {
      video.addEventListener('timeupdate', handleTimeUpdate);
    } else {
      video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
    }
    video.addEventListener('ended', handleEndedFull);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('ended', handleEndedFull);
      cancelAnimationFrame(rafRef.current);
      const cancelIdle = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (idle && cancelIdle) cancelIdle(idleId as number);
      else window.clearTimeout(idleId as number);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          preload="none"
          autoPlay
          muted
          playsInline
          aria-hidden="true"
          tabIndex={-1}
          className="w-full h-full object-cover object-center blur-[2px] scale-105"
        >
          <track kind="captions" srcLang="de" label="Keine Sprachinhalte" src="/media/hero-captions.vtt" default />
        </video>
        {/* Moderner Farbverlauf mit leichtem Blau-Akzent */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-800/80 to-primary/40" />
        {/* Horizontaler Verlauf für Tiefe */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
        {/* Subtiler Noise-Effekt für Textur */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />
        {/* Vignette-Effekt */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 pt-32 md:pt-40 lg:pt-32 pb-24 md:pb-0">
        <div className="max-w-4xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-fastlane/20 border border-fastlane/30 rounded-full px-4 py-2 mb-6"
          >
            <Zap className="w-4 h-4 text-fastlane" />
            <span className="text-white/90 text-sm font-medium">Express-Fertigung für Automatisierungstechnik</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white leading-tight mb-6"
          >
            Während Ihr Mitbewerber wartet,{' '}
            <span className="text-fastlane">fertigen wir bereits</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl"
          >
            Expressfertigung und kostenoptimierte Produktion für Automatisierungstechnik und Sondermaschinenbau
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-start gap-4 mb-16"
          >
            <Button variant="heroPrimary" size="xl" className="gap-2 text-sm md:text-base px-4 md:px-6 py-2.5 md:py-4 h-auto" asChild>
              <a href="/anfrage?type=fastlane">
                <Zap className="w-4 h-4 md:w-5 md:h-5" />
                FastLane Anfrage – Antwort in Minuten
              </a>
            </Button>
            <Button variant="heroSecondary" size="xl" className="gap-2 text-sm md:text-base px-4 md:px-6 py-2.5 md:py-4 h-auto" asChild>
              <a href="/anfrage?type=smart">
                <TrendingDown className="w-4 h-4 md:w-5 md:h-5" />
                Kostenoptimierte Anfrage
              </a>
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-6 md:gap-10"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-fastlane/20 flex items-center justify-center">
                <Zap className="w-6 h-6 text-fastlane" />
              </div>
              <div>
                <p className="text-white font-semibold">Fertigung oft am selben Tag</p>
                <p className="text-white/60 text-sm">Express-Produktion</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-smart/20 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-smart" />
              </div>
              <div>
                <p className="text-white font-semibold">Bis zu 40% Kostenersparnis</p>
                <p className="text-white/60 text-sm">Intelligente Kapazitätsplanung</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">Ein Ansprechpartner</p>
                <p className="text-white/60 text-sm">Für Ihr gesamtes Projekt</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator - Drehendes Zahnrad */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-2 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center"
      >
        {/* Zahnrad */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          className="w-10 h-10"
        >
          <svg viewBox="0 0 24 24" className="w-full h-full drop-shadow-lg">
            <defs>
              <linearGradient id="gearGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
                <stop offset="50%" stopColor="rgba(255,255,255,0.5)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.9)" />
              </linearGradient>
            </defs>
            {/* Zahnrad mit 8 Zähnen */}
            <path
              d="M12 1 L13.5 4 L17 3 L16 6.5 L20 7 L18 10 L21 12 L18 14 L20 17 L16 17.5 L17 21 L13.5 20 L12 23 L10.5 20 L7 21 L8 17.5 L4 17 L6 14 L3 12 L6 10 L4 7 L8 6.5 L7 3 L10.5 4 Z"
              fill="url(#gearGradient)"
            />
            {/* Innerer Kreis */}
            <circle cx="12" cy="12" r="4" fill="rgba(0,0,0,0.3)" />
            <circle cx="12" cy="12" r="2.5" fill="url(#gearGradient)" />
          </svg>
        </motion.div>
        
        {/* Animated Arrow */}
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="mt-3"
        >
          <ChevronDown className="w-5 h-5 text-white/50" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
